import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { DateValue } from '@react-types/calendar';
import { DatePicker, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { Upload } from 'lucide-react';

import { useReceivingTicketFiles } from './hooks/useReceivingTicketFiles';

import {
  $generateFileAccessURL,
  $materialsReconciliationForm,
  $updateMaterialsReconciliationForm,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { FilesTab } from '~/shared/components/files';
import { GenericFile } from '~/shared/types/file';

interface ReceivingTicketInfoProps {
  reconciliationId?: string;
}

export const ReceivingTicketInfo: React.FC<ReceivingTicketInfoProps> = ({
  reconciliationId,
}) => {
  const t = useTranslations();
  const formState = useAtomValue($materialsReconciliationForm);
  const updateForm = useSetAtom($updateMaterialsReconciliationForm);
  const [isFetchingUrls, setIsFetchingUrls] = React.useState(true);

  // Receiving ticket file mutations
  const receivingTicketMutations = useReceivingTicketFiles();
  const generateDownloadUrl = useSetAtom($generateFileAccessURL);

  // State for managing files with proper typing
  const [initialFiles, setInitialFiles] = React.useState<GenericFile[]>([]);

  // Initialize files from form state
  React.useEffect(() => {
    if (formState.receivingTicketFiles) {
      // Convert form state files to GenericFile format
      const genericFiles: GenericFile[] = formState.receivingTicketFiles.map(
        (file) => ({
          id: file.id,
          fileName: file.name,
          fileSize: file.fileSize || 0,
          mimeType:
            file.type === 'Pdf'
              ? 'application/pdf'
              : file.type === 'Image'
                ? 'image/jpeg'
                : 'text/plain',
          status: 'Active',
          downloadUrl: file.url,
          entityType: 'LoadReconciliation',
          entityId: reconciliationId,
          metadata: {
            entityType: 'LoadReconciliation',
            entityId: reconciliationId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        }),
      );

      generateDownloadUrl({
        fileIds: genericFiles.map((file) => file.id),
      })
        .then((result) => {
          const downloadResults =
            result.filesGenerateFileAccessURL?.results || [];

          const urlMap = new Map(
            downloadResults
              .filter((result) => result?.url)
              .map((result) => [result.fileId, result.url]),
          );

          // Update files with download URLs
          const updatedFiles = genericFiles.map((file) => ({
            ...file,
            downloadUrl: urlMap.get(file.id) || file.downloadUrl,
          }));

          setInitialFiles(updatedFiles);
        })
        .catch((error) => {
          console.error('Failed to generate download URLs:', error);
        })
        .finally(() => {
          setIsFetchingUrls(false);
        });
    } else {
      setIsFetchingUrls(false);
    }
  }, [reconciliationId]);

  const handleTicketNumberChange = (value: string) => {
    updateForm({ ticketNumber: value });
  };

  const handleLoadReceivedDateChange = (value: DateValue | null) => {
    updateForm({
      loadReceivedDate: value
        ? new CalendarDate(value.year, value.month, value.day)
        : undefined,
    });
  };

  const handleTicketIssueDateChange = (value: DateValue | null) => {
    updateForm({
      ticketIssueDate: value
        ? new CalendarDate(value.year, value.month, value.day)
        : undefined,
    });
  };

  const handleMillReceivedWeightPostDateChange = (value: DateValue | null) => {
    updateForm({
      millReceivedWeightPostDate: value
        ? new CalendarDate(value.year, value.month, value.day)
        : undefined,
    });
  };

  const handleFilesChange = (newFiles: GenericFile[]) => {
    // Transform GenericFile format back to form state format
    const transformedFiles = newFiles.map((file) => ({
      id: file.id,
      name: file.fileName,
      type: (file.mimeType === 'application/pdf'
        ? 'Pdf'
        : file.mimeType.startsWith('image/')
          ? 'Image'
          : 'Text') as 'Pdf' | 'Image' | 'Text',
      url: file.downloadUrl || '',
      confirmed: file.status === 'Active',
      fileSize: file.fileSize,
    }));

    updateForm({ receivingTicketFiles: transformedFiles });
  };

  return (
    <div className="@ReceivingTicketInfo p-6">
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          <TextField
            value={formState.ticketNumber || ''}
            onChange={handleTicketNumberChange}
            label={t.materialsReconciliation.receivingTicketInfo.ticketNumber}
            placeholder={
              t.materialsReconciliation.receivingTicketInfo
                .ticketNumberPlaceholder
            }
            className="[&_input]:bg-white"
          />
          <DatePicker
            value={formState.loadReceivedDate || null}
            label={
              t.materialsReconciliation.receivingTicketInfo.loadReceivedDate
            }
            onChange={handleLoadReceivedDateChange}
            className="[&_[data-slot=field-group]]:bg-white [&_[role=group]]:bg-white"
          />
          <DatePicker
            value={formState.ticketIssueDate || null}
            label={
              t.materialsReconciliation.receivingTicketInfo.ticketIssueDate
            }
            onChange={handleTicketIssueDateChange}
            className="[&_[data-slot=field-group]]:bg-white [&_[role=group]]:bg-white"
          />
          <DatePicker
            value={formState.millReceivedWeightPostDate || null}
            label={
              t.materialsReconciliation.receivingTicketInfo
                .millReceivedWeightPostDate
            }
            onChange={handleMillReceivedWeightPostDateChange}
            className="[&_[data-slot=field-group]]:bg-white [&_[role=group]]:bg-white"
          />
        </div>

        {/* Files Section */}
        <div>
          {reconciliationId && !isFetchingUrls ? (
            <FilesTab
              entityId={reconciliationId}
              files={initialFiles}
              onFilesChange={handleFilesChange}
              mutations={receivingTicketMutations}
              title={
                t.materialsReconciliation.receivingTicketInfo
                  .loadReconciliationPhotos
              }
              dropZoneText={{
                primary:
                  t.materialsReconciliation.receivingTicketInfo.dropZonePrimary,
                secondary:
                  t.materialsReconciliation.receivingTicketInfo
                    .dropZoneSecondary,
              }}
            />
          ) : (
            <div className="rounded-lg border-2 border-dashed border-neutral/30 p-8">
              <div className="text-center">
                <Upload className="mx-auto size-12 text-neutral/40" />
                <p className="mt-2 text-sm text-neutral/60">
                  {
                    t.materialsReconciliation.receivingTicketInfo
                      .saveFirstMessage
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
